import {
  ADDRESS_LINE_MAX,
  ADDRESS_POSTAL_CODE_MAX,
  countryOf,
  fold,
  hasRegion,
  poBoxNumber,
  postalKindOf,
  postalMatches,
  postalRequired,
  regionRequired,
  tidyPostalCode,
  upperInvariant,
  type AddressRecord,
  type CountryEntry,
  type DoctorAddress,
  type GeoCity,
  type GeoLookup,
} from '../geo/address';
import { phoneCandidate, phoneWritten, type PhoneReader, type ReadPhone } from './clients';

/**
 * Converting the telephone numbers and addresses that were typed as free text
 * into the international form. The request of 6 October 2026 (fifth),
 * section 2, "Existing data".
 *
 * Nothing is converted on its own. Management reads the list of what would
 * happen to every doctor (`GET /doctors/contact-conversion`), and then presses
 * the button (`POST`, which does exactly what the list said). The rule is to
 * convert only what is certain and to guess at nothing: a number is converted
 * when it carries its country's code and is a valid number; an address when
 * its last part names a country, exactly one other part is exactly one city of
 * that country, and something is left for the street. Everything else is left
 * as it was typed and marked for a person to put right. The old text itself is
 * never changed, converted or not.
 *
 * The rule is the server's, step for step and sentence for sentence. It is
 * pure: the telephone library and the list of cities are handed in.
 */

export const CONVERSION_OUTCOMES = ['converted', 'review'] as const;
export type ConversionOutcome = (typeof CONVERSION_OUTCOMES)[number];

/** Why a number or an address is left for review, in the server's words. */
export const CONVERSION_REASON = {
  /** A number with neither a `+` nor a leading `00`: nothing says which country it is in. */
  noCountryCode: 'Written without a country code',
  invalidPhone: 'Not a valid phone number',
  countryNotNamed: 'The country is not named',
  cityNotInList: 'The city is not in the list',
  manyCities: 'More than one city fits',
  noStreet: 'Nothing is left for the street line',
  streetTooLong: 'The street line is too long',
  /** A country that requires a region, and a city whose own region the list of regions does not have. */
  regionNotInList: "The city's state or region is not in the list",
  postalMissing: 'The postal code is missing',
} as const;

/** What would happen, or did, to one doctor's old telephone text. */
export interface PhoneConversion {
  /** The old text, as stored. */
  before: string;
  outcome: ConversionOutcome;
  /** The number as E.164 when it converts, else null. */
  after: string | null;
  /** The same number as people read one, else null. */
  afterDisplay: string | null;
  /** Why it is left for review, else null. */
  reason: string | null;
}

/** What would happen, or did, to one doctor's old address text. */
export interface AddressConversion {
  before: string;
  outcome: ConversionOutcome;
  /**
   * The address it becomes; or, left for review, as much of it as is certain,
   * with no street line; or null when not even the country is.
   */
  after: DoctorAddress | null;
  reason: string | null;
}

/** One doctor with something waiting to be converted. A field with nothing waiting is null. */
export interface ConversionItem {
  id: string;
  code: string;
  name: string;
  clinic: string | null;
  phone: PhoneConversion | null;
  address: AddressConversion | null;
}

export interface ConversionCounts {
  /** Doctors with something waiting. */
  doctors: number;
  phonesConverted: number;
  phonesForReview: number;
  addressesConverted: number;
  addressesForReview: number;
}

/** `GET` and `POST /doctors/contact-conversion`: the counts, and every doctor with something waiting, by name. */
export interface ContactConversion {
  counts: ConversionCounts;
  items: ConversionItem[];
}

/** What the items come to: the five counts above the list. */
export function conversionCounts(items: readonly ConversionItem[]): ConversionCounts {
  const count = (field: 'phone' | 'address', outcome: ConversionOutcome) =>
    items.filter((item) => item[field]?.outcome === outcome).length;

  return {
    doctors: items.length,
    phonesConverted: count('phone', 'converted'),
    phonesForReview: count('phone', 'review'),
    addressesConverted: count('address', 'converted'),
    addressesForReview: count('address', 'review'),
  };
}

/* ---- the telephone number ---- */

/** What the old telephone text comes to: the number it is, or why it is left. */
export interface ConvertedPhone {
  outcome: ConversionOutcome;
  number: ReadPhone | null;
  reason: string | null;
}

/**
 * The old telephone text, converted or left. White space, hyphens, dots and
 * round brackets go, and a leading `00` is the `+` it stands for. What is
 * then a plus and digits and nothing else, and a valid number, converts. A
 * number with neither a plus nor the two noughts is left for want of a
 * country code, and never given one: +971 is a guess, and a wrong number
 * saved as a right one is worse than text somebody can read. One written with
 * an extension or with letters is no number here, as it is none on a save.
 */
export function convertPhone(text: string, read: PhoneReader): ConvertedPhone {
  const bare = phoneCandidate(text);
  const candidate = bare.startsWith('00') ? `+${bare.slice(2)}` : bare;

  if (!candidate.startsWith('+')) {
    return { outcome: 'review', number: null, reason: CONVERSION_REASON.noCountryCode };
  }
  const number = phoneWritten(candidate) ? read(candidate) : null;
  return number === null
    ? { outcome: 'review', number: null, reason: CONVERSION_REASON.invalidPhone }
    : { outcome: 'converted', number, reason: null };
}

/* ---- the address ---- */

/** The ways the old text names four countries without spelling them out, by fold, and the country each means. */
const COUNTRY_ALIASES: ReadonlyMap<string, string> = new Map([
  ['uae', 'AE'],
  ['u.a.e', 'AE'],
  ['u.a.e.', 'AE'],
  ['ksa', 'SA'],
  ['uk', 'GB'],
  ['u.k.', 'GB'],
  ['usa', 'US'],
  ['u.s.a.', 'US'],
  ['us', 'US'],
]);

/** The country a part of the old text names: by its name in the list, or by one of the aliases. */
function countryNamed(part: string, countries: readonly CountryEntry[]): CountryEntry | undefined {
  const folded = fold(part);
  return (
    countries.find((country) => fold(country.name) === folded) ??
    countryOf(countries, COUNTRY_ALIASES.get(folded))
  );
}

/** What the old address text comes to: the parts that are certain, and why it is left when it is. */
export interface ConvertedAddress {
  outcome: ConversionOutcome;
  /**
   * Converted: the whole address. Left for review: what was certain before
   * the step that failed (the country, then the city and its region, then the
   * postal code), with no street line. Null when the country is not named.
   */
  record: AddressRecord | null;
  reason: string | null;
}

/**
 * The old address text, converted or left, in five steps. The text is split
 * on line breaks, commas and semicolons into parts.
 *
 *   1. The LAST part names a country, by its name or by one of the aliases
 *      (UAE, KSA, UK, USA and their spellings). Otherwise it is left.
 *   2. Exactly ONE of the other parts is the name of exactly one city of that
 *      country in the list. None, two, or a name several cities share: left.
 *   3. In a country whose addresses carry a PO box, the one part written
 *      `PO Box 123` is the box. In a country whose postal codes have a
 *      pattern, the one remaining part that is such a code is it. Exactly one
 *      in both: with two, neither is taken and both stay in the street line.
 *      A box or a code past twenty characters is not taken either.
 *   4. What is left, joined with commas, is the street line. Nothing left, or
 *      more than two hundred characters: left for review.
 *   5. The region, where the country has one, is the city's own. A country
 *      that requires a region, and a city whose own the list does not have:
 *      left for review. Then a country that requires a postal code and has
 *      none here: left for review.
 *
 * It stops at the first step that fails, and what is certain is what the
 * steps before it settled.
 *
 * The area and the second line are never filled in: nothing in free text says
 * which part they would be.
 */
export function convertAddress(
  text: string,
  countries: readonly CountryEntry[],
  geo: GeoLookup,
): ConvertedAddress {
  const parts = text
    .split(/[\r\n,;]+/)
    .map((part) => part.trim())
    .filter((part) => part !== '');

  const review = (record: AddressRecord | null, reason: string): ConvertedAddress => ({
    outcome: 'review',
    record,
    reason,
  });

  // 1. The country, named last.
  const last = parts.at(-1);
  const country = last === undefined ? undefined : countryNamed(last, countries);
  if (country === undefined) return review(null, CONVERSION_REASON.countryNotNamed);

  const record: AddressRecord = {
    country: country.code,
    region: null,
    regionCode: null,
    city: null,
    cityId: null,
    area: null,
    line1: null,
    line2: null,
    postalCode: null,
  };

  // 2. The city: one part, one city.
  let rest = parts.slice(0, -1);
  const fits = rest
    .map((part) => ({ part, cities: geo.citiesNamed(country.code, fold(part)) }))
    .filter((fit) => fit.cities.length > 0);
  const only = fits.length === 1 ? fits[0] : undefined;
  const city: GeoCity | undefined = only?.cities.length === 1 ? only.cities[0] : undefined;
  if (only === undefined || city === undefined) {
    return review(
      record,
      fits.length === 0 ? CONVERSION_REASON.cityNotInList : CONVERSION_REASON.manyCities,
    );
  }
  rest = rest.filter((part) => part !== only.part);
  record.city = city.name;
  record.cityId = city.id;

  // 5, as far as it can be known already: the region is the city's own.
  const region =
    hasRegion(country) && city.regionCode !== null
      ? geo.region(country.code, city.regionCode)
      : undefined;
  record.region = region?.name ?? null;
  record.regionCode = region?.code ?? null;

  // 3. The PO box, or the postal code: the one part that is one, when exactly one is.
  const coded =
    postalKindOf(country) === 'pobox'
      ? rest.filter((part) => poBoxNumber(part) !== null)
      : country.postal.pattern === undefined
        ? []
        : rest.filter((part) => postalMatches(country, upperInvariant(part)));
  const code = coded.length === 1 ? coded[0] : undefined;
  const postalCode = code === undefined ? null : tidyPostalCode(code, country);
  if (postalCode !== null && postalCode.length <= ADDRESS_POSTAL_CODE_MAX) {
    rest = rest.filter((part) => part !== code);
    record.postalCode = postalCode;
  }

  // 4. The street line is what is left.
  const street = rest.join(', ');
  if (street === '') return review(record, CONVERSION_REASON.noStreet);
  if (street.length > ADDRESS_LINE_MAX) return review(record, CONVERSION_REASON.streetTooLong);

  // 5. A country that requires a region has to have the city's own, and one
  // that requires a postal code has to have one.
  if (regionRequired(country) && record.region === null) {
    return review(record, CONVERSION_REASON.regionNotInList);
  }
  if (postalRequired(country) && record.postalCode === null) {
    return review(record, CONVERSION_REASON.postalMissing);
  }

  return { outcome: 'converted', record: { ...record, line1: street }, reason: null };
}
